import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { startWith } from 'rxjs';
import { LanguageService } from '../../../../../core/services/language.service';
import { DropdownComponent } from '../../../../../osp-ui/components/form/dropdown/dropdown.component';
import { ErrorComponent } from '../../../../../osp-ui/components/form/form-field/components/error/error.component';
import { LabelComponent } from '../../../../../osp-ui/components/form/form-field/components/label/label.component';
import { FormFieldComponent } from '../../../../../osp-ui/components/form/form-field/form-field.component';
import { MapComponent } from '../../../../../shared/components/maps/components/map/map.component';
import { ViewGlobalGeofence } from '../../../../../shared/contract/view-global-geofence.contract';
import { GeofenceAlarmTrigger } from '../../../../../shared/enums/geofence-alarm-trigger.enum';
import { AlarmSettingsFieldData } from '../../../contract/alarm-settings-field-data.contract';
import { AlarmSettingsBase } from '../alarm-settings-base.class';
import { AlarmSettingsFormControls } from '../contract/alarm-settings-form-controls.contract';

@Component({
  selector: 'osp-geofence-settings',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DropdownComponent,
    MapComponent,
    TranslateModule,
    FormFieldComponent,
    LabelComponent,
    ErrorComponent,
  ],
  templateUrl: './geofence-settings.component.html',
  styleUrl: './geofence-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GeofenceSettingsComponent extends AlarmSettingsBase implements OnInit, AfterViewInit {
  private destroyRef = inject(DestroyRef);

  @Input() set form(form: FormGroup<AlarmSettingsFormControls>) {
    this.settingsForm = form;
  }
  @Input() set fieldData(fieldData: AlarmSettingsFieldData) {
    this.alarmSettingsFieldData = fieldData;
    this.setLabels();
  }
  globalGeofences = input<ViewGlobalGeofence[]>([]);
  geoColor = input('red');

  protected initialCenter = signal<google.maps.LatLngLiteral | undefined>(undefined);
  protected geoLabel = '';
  protected geoTriggerLabel = '';
  protected geoTriggerExplanation: string = '';
  protected triggerOptions = [
    {
      value: GeofenceAlarmTrigger.ENTERING,
      label: this.languageService.getInstant('modules.notification.alarmRuleAddEdit.triggerEnter'),
    },
    {
      value: GeofenceAlarmTrigger.LEAVING,
      label: this.languageService.getInstant('modules.notification.alarmRuleAddEdit.triggerLeave'),
    },
  ];

  private map: google.maps.Map | undefined;
  private displayedPolygon: google.maps.Polygon | undefined;
  private globalGeofencesMap = computed(
    () => new Map<string, ViewGlobalGeofence>(this.globalGeofences().map((geo) => [geo.globalGeofenceId, geo])),
  );
  private isViewInitialized = signal(false);
  private isMapInit = signal(false);

  constructor(private languageService: LanguageService) {
    super();
    effect(() => {
      if (this.isViewInitialized() && this.isMapInit()) {
        this.initListeners();
      }
    });
  }

  ngOnInit(): void {
    if (navigator?.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        if (this.displayedPolygon) {
          return;
        }
        this.initialCenter.set({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      });
    }
  }

  ngAfterViewInit(): void {
    this.isViewInitialized.set(true);
  }

  public mapReady(map: google.maps.Map): void {
    this.map = map;
    this.isMapInit.set(true);
  }

  private initListeners(): void {
    if (!this.geofenceControl) {
      return;
    }
    this.geofenceControl.valueChanges
      .pipe(startWith(this.geofenceControl.value), takeUntilDestroyed(this.destroyRef))
      .subscribe((geofenceId: string) => this.valueChangesGeofence(geofenceId));
  }

  private setLabels(): void {
    this.geoLabel = this.getFieldLabel(this.controlNames.SETTINGS_GLOBAL_GEOFENCE);
    this.geoTriggerLabel = this.getFieldLabel(this.controlNames.SETTINGS_GEOFENCE_TRIGGER);
    this.geoTriggerExplanation = this.getFieldTooltipText(this.controlNames.SETTINGS_GEOFENCE_TRIGGER);
  }

  private valueChangesGeofence(geofenceId: string): void {
    if (this.displayedPolygon) {
      this.displayedPolygon.setMap(null);
    }
    if (geofenceId) {
      const geo = this.globalGeofencesMap().get(geofenceId);
      this.showSingleGeofence(geo);
    }
  }

  private showSingleGeofence(viewGeofence: ViewGlobalGeofence | undefined): void {
    if (!viewGeofence) {
      return;
    }
    const polygon = this.mapPolygonMapper(viewGeofence);
    const bounds = new google.maps.LatLngBounds();

    polygon.setVisible(true);
    polygon.getPath().forEach((coord: google.maps.LatLng) => bounds.extend(coord));
    if (this.map) {
      this.map.panToBounds(bounds);
      this.map.fitBounds(bounds);
    }

    this.displayedPolygon = polygon;
  }

  private mapPolygonMapper(geofence: ViewGlobalGeofence): google.maps.Polygon {
    return new google.maps.Polygon({
      paths: geofence.coordinates.map(({ lat, lon }) => ({ lat, lng: lon })),
      strokeColor: this.geoColor(),
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: this.geoColor(),
      fillOpacity: 0.5,
      map: this.map,
      clickable: false,
    });
  }
}
